/**
 * Pending-charges API wrapper (v2 — Upcoming Charges, DNKADM-0145).
 *
 * FORWARD CONTRACT — dink-api handoff DNKAPI-0279 (specced, NOT yet deployed).
 * This client is built to the 0279 wire shape; where the shipped API differs, the
 * shipped API wins (report the delta). A not-yet-deployed backend is not a blocker:
 * every call degrades gracefully (the list parses defensively to an empty page, the
 * per-reservation read returns null on 404).
 *
 * A **pending charge** is a deferred card charge created when an admin/member marks
 * a reservation Showed/No-show on a card-on-file service; it runs ~30 min after the
 * reservation ends unless waived or charged early. Charge-mode and credit
 * reservations never produce one.
 *
 * ENDPOINTS (all `/v2/admin/**` — admin JWT, bearer-only; `request()` routes them:
 * bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry):
 *  - LIST   `GET  /v2/admin/pending-charges` — tenant-scoped, paged (Spring `Page`
 *           envelope `{ content, page, size, totalElements, totalPages }`, parsed
 *           defensively — mirroring `api/configAudit.ts`), enriched.
 *  - READ   `GET  /v2/admin/reservations/{uuid}/pending-charge` → the item or null.
 *  - ADJUST `PATCH /v2/admin/pending-charges/{id}` `{ amountMinor }` (pending-only,
 *           else 409).
 *  - WAIVE  `POST /v2/admin/reservations/{uuid}/pending-charge/waive` → cancels the
 *           reservation's pending charge (pending-only, else 409).
 *  - CHARGE-NOW `POST /v2/admin/pending-charges/{id}/charge-now` → runs now; returns
 *           the updated item (`charged` + `paymentRef`, or `failed` + `failureCode`).
 *
 * Money-moving actions (adjust, charge-now) are gated like refunds server-side (the
 * same admin role — no separate UI permission). A `409` (CONFLICT) means the charge
 * already ran or was waived; the caller surfaces a fixed message and refetches.
 *
 * DEFENSIVE PARSE — mirrors `api/configAudit.ts` / `api/audit.ts`: each scalar field
 * is read from a small candidate-key list, a missing/wrong-typed field degrades to
 * null (never throws), an item with no `id`/`reservationUuid` is dropped, and a
 * non-array/absent `content` yields an empty page.
 */
import { request } from './client';
import { ApiError } from './errors';
import { parseDeclineReason, type DeclineReason } from '@/utils/declineReason';

/**
 * The pending-charge lifecycle status. Carried as a plain string (a known status
 * passes through; an unrecognized upstream value still renders) — the UI treats
 * only `pending` as actionable.
 */
export type PendingChargeStatus = 'pending' | 'charged' | 'cancelled' | 'failed' | (string & {});

/** The known statuses, in a stable display order (drives the status filter). */
export const PENDING_CHARGE_STATUSES: readonly ['pending', 'charged', 'cancelled', 'failed'] = [
  'pending',
  'charged',
  'cancelled',
  'failed',
];

/**
 * One parsed pending charge (DNKAPI-0279). Enriched fields (contact/service/card…)
 * may be null on the wire; `amountMinor` is the charge amount in the currency's
 * minor units, `currency` its ISO-4217 code.
 */
export interface PendingCharge {
  /** Stable numeric id (the action-endpoint path segment). */
  id: number;
  /** The reservation this charge belongs to (the View-reservation deep-link key). */
  reservationUuid: string;
  /** Lifecycle status; only `pending` is actionable. */
  status: PendingChargeStatus;
  /** Charge amount in the currency's MINOR units (e.g. cents). */
  amountMinor: number;
  /** ISO-4217 currency code (NEVER assume USD). */
  currency: string;
  /** When the charge is scheduled to run (ISO instant), or null. Sort key. */
  scheduledAt: string | null;
  contactName: string | null;
  contactEmail: string | null;
  serviceName: string | null;
  variationName: string | null;
  /** Reservation start/end (ISO-with-offset), or null. */
  start: string | null;
  end: string | null;
  cardBrand: string | null;
  cardMasked: string | null;
  /** Set once `charged` (the payment reference). */
  paymentRef: string | null;
  /** Set once `failed` (the failure code; not auto-retried). */
  failureCode: string | null;
  /**
   * Why the card was declined once `failed` (DNKAPI-0676), or null when unknown, not a
   * decline, or a value this build does not know. `@/utils/declineReason` gives the line.
   */
  declineReason?: DeclineReason | null;
}

/** A parsed page of pending charges (the Spring `Page` envelope, normalized). */
export interface PendingChargesPage {
  items: PendingCharge[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// --- Defensive parsing helpers (mirror api/configAudit.ts) ---------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A finite number (accepts numeric strings), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** First candidate key that yields a non-null trimmed string, else null. */
function pickText(o: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const k of keys) {
    const v = asText(o[k]);
    if (v !== null) return v;
  }
  return null;
}

/**
 * Parse one raw item into a {@link PendingCharge}, or null when it lacks a usable
 * `id` or `reservationUuid` (dropped from the feed rather than rendered broken).
 * Every other field degrades to null (or `amountMinor` to 0) — never throws.
 */
function parsePendingCharge(raw: unknown): PendingCharge | null {
  const o = isObject(raw) ? raw : {};
  const id = asNumber(o['id']);
  const reservationUuid = pickText(o, ['reservationUuid', 'reservation_uuid', 'reservationId']);
  if (id === null || reservationUuid === null) return null;
  return {
    id,
    reservationUuid,
    // Carry the status text as-is (an unknown value still renders; absent → '',
    // which reads as non-actionable so no action buttons show on a malformed row).
    status: pickText(o, ['status']) ?? '',
    amountMinor: asNumber(o['amountMinor'] ?? o['amount_minor']) ?? 0,
    currency: pickText(o, ['currency']) ?? '',
    scheduledAt: pickText(o, ['scheduledAt', 'scheduled_at']),
    contactName: pickText(o, ['contactName', 'contact_name']),
    contactEmail: pickText(o, ['contactEmail', 'contact_email']),
    serviceName: pickText(o, ['serviceName', 'service_name']),
    variationName: pickText(o, ['variationName', 'variation_name']),
    start: pickText(o, ['start', 'startTime', 'start_time']),
    end: pickText(o, ['end', 'endTime', 'end_time']),
    cardBrand: pickText(o, ['cardBrand', 'card_brand']),
    cardMasked: pickText(o, ['cardMasked', 'card_masked', 'cardLast4', 'last4']),
    paymentRef: pickText(o, ['paymentRef', 'payment_ref']),
    failureCode: pickText(o, ['failureCode', 'failure_code']),
    declineReason: parseDeclineReason(o['declineReason']),
  };
}

/**
 * Parse the array of items defensively. The live Spring `Page` publishes the rows
 * under `content`; `items`/`results` are tolerated as fallbacks. A non-array (or
 * missing) field yields an empty list rather than throwing. Malformed items (no
 * `id`/`reservationUuid`) are dropped.
 */
function parseItems(body: Record<string, unknown>): PendingCharge[] {
  const candidates = [body['content'], body['items'], body['results']];
  const arr =
    candidates.find((c): c is unknown[] => Array.isArray(c)) ??
    (Array.isArray(body) ? (body as unknown[]) : null);
  if (!arr) return [];
  return arr
    .map(parsePendingCharge)
    .filter((c): c is PendingCharge => c !== null);
}

/**
 * Parse the `Page` envelope (mirrors `configAudit`'s `parsePage`): rows from
 * `content`, `totalElements` (legacy `total` fallback), and `page`/`size`/
 * `totalPages` with the caller's request values as fallbacks. Items are re-sorted
 * by `scheduledAt` ascending (soonest first) defensively.
 */
function parsePendingChargesPage(
  body: unknown,
  fallbackPage: number,
  fallbackSize: number,
): PendingChargesPage {
  const o = isObject(body) ? body : {};
  const items = sortSoonestFirst(parseItems(o));
  return {
    items,
    page: asNumber(o['page']) ?? fallbackPage,
    size: asNumber(o['size']) ?? fallbackSize,
    totalElements: asNumber(o['totalElements']) ?? asNumber(o['total']) ?? items.length,
    totalPages: asNumber(o['totalPages']) ?? 1,
  };
}

/** Parse an ISO/epoch instant to a sortable number; unknown → +Infinity (sorts last). */
function scheduledOrder(ts: string | null): number {
  if (ts === null) return Number.POSITIVE_INFINITY;
  const ms = Date.parse(ts);
  if (Number.isFinite(ms)) return ms;
  const num = Number(ts);
  return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
}

/**
 * Re-sort by `scheduledAt` ascending (soonest first). The endpoint is documented
 * as already soonest-first; we re-sort client-side defensively. Items with no
 * parseable `scheduledAt` sort to the end, preserving their relative order (stable).
 */
function sortSoonestFirst(items: PendingCharge[]): PendingCharge[] {
  return items
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      const diff = scheduledOrder(a.c.scheduledAt) - scheduledOrder(b.c.scheduledAt);
      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ c }) => c);
}

// --- Endpoints -----------------------------------------------------------------

export interface ListPendingChargesParams {
  /** Server-side status filter (default `pending`); sent only when set. */
  status?: PendingChargeStatus;
  /** Owned-location filter; sent as `?locationId=` only when set. */
  locationId?: string;
  /** Inclusive scheduled-window lower bound (ISO instant); sent only when set. */
  scheduledFrom?: string;
  /** Inclusive scheduled-window upper bound (ISO instant); sent only when set. */
  scheduledTo?: string;
  /** Filter to one reservation's charge; sent only when set. */
  reservationUuid?: string;
  page?: number;
  size?: number;
}

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 20;

/**
 * `GET /v2/admin/pending-charges` → a parsed, soonest-first page of pending charges
 * (DNKAPI-0279). Each filter is sent ONLY when set (omitting `status` returns the
 * backend default — the caller passes `status: 'pending'` for the imminent queue).
 * Throws a typed `ApiError` on a non-2xx response.
 */
export async function listPendingCharges(
  params: ListPendingChargesParams = {},
): Promise<PendingChargesPage> {
  const page = params.page ?? DEFAULT_PAGE;
  const size = params.size ?? DEFAULT_PAGE_SIZE;
  const body = await request<unknown>('GET', '/v2/admin/pending-charges', {
    query: {
      page,
      size,
      status: asText(params.status) ?? undefined,
      locationId: asText(params.locationId) ?? undefined,
      scheduledFrom: asText(params.scheduledFrom) ?? undefined,
      scheduledTo: asText(params.scheduledTo) ?? undefined,
      reservationUuid: asText(params.reservationUuid) ?? undefined,
    },
  });
  return parsePendingChargesPage(body, page, size);
}

/**
 * `GET /v2/admin/reservations/{uuid}/pending-charge` → the reservation's pending
 * charge, or null (404/empty body). A 404 is a normal "no charge" answer here, so
 * it is swallowed to null; every other non-2xx propagates as a typed `ApiError`.
 */
export async function getReservationPendingCharge(uuid: string): Promise<PendingCharge | null> {
  try {
    const body = await request<unknown>(
      'GET',
      `/v2/admin/reservations/${encodeURIComponent(uuid)}/pending-charge`,
    );
    return body === null ? null : parsePendingCharge(body);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'NOT_FOUND') return null;
    throw err;
  }
}

/**
 * `PATCH /v2/admin/pending-charges/{id}` `{ amountMinor }` → the updated charge.
 * `amountMinor` must be > 0; a non-`pending` charge yields a `409` (CONFLICT).
 */
export interface AdjustPendingChargeInput {
  amountMinor: number;
}

export async function adjustPendingCharge(
  id: number,
  input: AdjustPendingChargeInput,
): Promise<PendingCharge | null> {
  if (!Number.isFinite(input.amountMinor) || input.amountMinor <= 0) {
    throw new ApiError('VALIDATION', 400, {
      details: { message: 'Amount must be greater than 0; waive the charge to cancel it.' },
    });
  }
  const body = await request<unknown>('PATCH', `/v2/admin/pending-charges/${id}`, {
    json: { amountMinor: input.amountMinor },
  });
  return body === null ? null : parsePendingCharge(body);
}

/**
 * `POST /v2/admin/reservations/{uuid}/pending-charge/waive` → the reservation's
 * charge is cancelled (it won't run). A missing/non-pending charge yields a `409`.
 */
export async function waiveReservationPendingCharge(uuid: string): Promise<void> {
  await request<unknown>(
    'POST',
    `/v2/admin/reservations/${encodeURIComponent(uuid)}/pending-charge/waive`,
  );
}

/**
 * `POST /v2/admin/pending-charges/{id}/charge-now` → runs the charge now and returns
 * the updated item (`charged` + `paymentRef`, or `failed` + `failureCode`). A
 * non-`pending` charge yields a `409` (CONFLICT).
 */
export async function chargePendingChargeNow(id: number): Promise<PendingCharge | null> {
  const body = await request<unknown>('POST', `/v2/admin/pending-charges/${id}/charge-now`);
  return body === null ? null : parsePendingCharge(body);
}
